import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import {
  FileDataDisplay,
  type FileDataDisplayProps,
} from "#src/components/DataDisplay/FileDataDisplay";
import {
  GeocoordinateDataDisplay,
  type GeocoordinateDataDisplayProps,
} from "#src/components/DataDisplay/GeocoordinateDataDisplay";
import { DefaultLocalizationMapping } from "#src/hooks/LocalizationProvider";
import useLocalization from "#src/hooks/useLocalization";
import {
  linkToDeviceDetail,
  linkToEquipmentDetail,
  linkToFacilities,
  linkToFormSubmissionDetail,
} from "#src/Routers/links";
import { linkToEventCategoryDetail } from "#src/routes/events/event-categories/[eventCategoryId]";
import { linkToFormCategoryDetails } from "#src/routes/forms/categories/[categoryId]";
import { linkToFormTemplateDetail } from "#src/routes/forms/categories/[categoryId]/templates/[formTemplateId]";
import { FlowDetailRoute } from "#src/routes/organization/flows/[flowId]/detail";
import { NetworkDetailRoute } from "#src/routes/organization/networks/[networkId]/detail";
import { linkToRoleDetailPage } from "#src/routes/settings/roles-and-permissions/roles/[roleId]/detail";
import { linkToUserDetailPage } from "#src/routes/settings/users/detail";
import { UserGroupDetailsRoutePath } from "#src/routes/settings/users/groups/[groupId]/details";
import { UserGroupCategoryDetailsRoutePath } from "#src/routes/settings/users/groups/categories/[categoryId]/details";
import { WorkflowDetailsRoutePath } from "#src/routes/workflows/all/[workflowId]/detail";
import { WorkflowCategoryDetailsRoutePath } from "#src/routes/workflows/categories/[workflowCategoryId]";
import { WorkflowTemplateDetailsRoutePath } from "#src/routes/workflows/templates/[workflowTemplateId]";
import { useMultiLookupAssetDetails } from "#src/utils/lookups";
import {
  ArrayDataDisplay,
  BooleanDataDisplay,
  DateDataDisplay,
  DateRangeDataDisplay,
  IntegerDataDisplay,
  NumberDataDisplay,
  TextDataDisplay,
  type ArrayDataDisplayProps,
  type BooleanDataDisplayProps,
  type DataDisplayProps,
  type DateDataDisplayProps,
  type DateRangeDataDisplayProps,
  type IntegerDataDisplayProps,
  type NumberDataDisplayProps,
  type TextDataDisplayProps,
} from "@validereinc/common-components";
import {
  AssetType,
  AssetTypeType,
  FileUploadedSchema,
  LookupAnswerValueSchema,
  LookupAnswerValueType,
  LookupWithDetailsValueSchema,
  LookupWithDetailsValueType,
  LookupWithSingleAttributeRefValueSchema,
  LookupWithSingleAttributeRefValueType,
  ResourceLookupType,
  Resources,
  ResourceType,
  type AttributeDataTypeType,
  type FileUploadedType,
} from "@validereinc/domain";
import {
  DateTimeValueSchemas,
  GeocoordinateSimpleValueSchema,
} from "@validereinc/utilities";
import { isPlainObject } from "es-toolkit/compat";
import React, { useMemo } from "react";
import { z } from "zod";

export type DataTypeToDataDisplayProps = {
  [K in AttributeDataTypeType]: K extends "boolean"
    ? Omit<BooleanDataDisplayProps, "value">
    : K extends "number"
      ? Omit<NumberDataDisplayProps, "value">
      : K extends "integer"
        ? Omit<IntegerDataDisplayProps, "value">
        : K extends "multi-pick-list" | "number-array" | "multi-file"
          ? Omit<ArrayDataDisplayProps, "value">
          : K extends "multi-lookup"
            ? Omit<ArrayDataDisplayProps, "value"> & {
                lookupEntityType?: ResourceLookupType;
              }
            : K extends "file"
              ? Omit<FileDataDisplayProps, "value">
              : K extends "geo_point"
                ? Omit<GeocoordinateDataDisplayProps, "value">
                : K extends "date" | "date-time"
                  ? Omit<DateDataDisplayProps, "value">
                  : K extends "date-time-range"
                    ? Omit<DateRangeDataDisplayProps, "value">
                    : Omit<TextDataDisplayProps, "value">;
};

/**
 * A component that fetches asset details for a list of IDs and renders each as
 * a routing link. Used by the `multi-lookup` case in {@link displayValueByDataType}
 * when the value contains raw UUID strings rather than enriched lookup objects.
 */
const MultiLookupDataDisplay = ({
  ids,
  entityType,
  emptyText,
  ...arrayDisplayProps
}: {
  ids: string[];
  entityType: ResourceLookupType;
  emptyText?: string;
} & Omit<ArrayDataDisplayProps, "value">) => {
  const idsByEntityType = useMemo(
    () => new Map([[entityType, new Set(ids)]]),
    [entityType, ids]
  );
  const { assetDetailsMap, isLoading } =
    useMultiLookupAssetDetails(idsByEntityType);

  const links = isLoading
    ? []
    : ids.flatMap((id) => {
        const asset = assetDetailsMap.get(id);
        if (!asset) return [];
        return [getLookupLink(id, asset.name, asset.entity_type)];
      });

  return (
    <ArrayDataDisplay
      value={links}
      isLoading={isLoading}
      emptyText={emptyText}
      {...arrayDisplayProps}
    />
  );
};

/**
 * Display a value using the data type definition describing the type of the value
 * @param value the value to render
 * @param dataType the type of the value @see {@link AttributeDataTypeType}
 * @param opts additional display options shared among all renderers, or that are specific to one renderer
 * @returns renders the value using the appropriate renderer for the provided type
 */
export const displayValueByDataType = <T extends AttributeDataTypeType>(
  value: any,
  dataType: T,
  opts?: DataTypeToDataDisplayProps[T]
): React.ReactNode => {
  switch (dataType) {
    case "boolean": {
      return (
        <BooleanDataDisplay
          value={value}
          {...(opts ?? {})}
        />
      );
    }
    case "multi-pick-list":
      return (
        <ArrayDataDisplay
          value={value ?? []}
          {...(opts ?? {})}
        />
      );
    case "multi-file": {
      const multiFileParseResult = z.array(FileUploadedSchema).safeParse(value);

      if (!multiFileParseResult.success) return opts?.emptyText ?? null;

      return (
        <ArrayDataDisplay
          value={multiFileParseResult.data.map((file) => (
            <FileDataDisplay
              key={file.ref}
              fileName={file.name}
              fileId={file.ref}
              prefetch={false}
              alignment={opts?.alignment ?? "left"}
              style={{ display: "inline-flex" }}
            />
          ))}
          {...(opts ?? {})}
        />
      );
    }
    case "file": {
      const singleFileParseResult = FileUploadedSchema.safeParse(value);

      if (!singleFileParseResult.success) return opts?.emptyText ?? null;

      return (
        <FileDataDisplay
          fileName={singleFileParseResult.data.name}
          fileId={singleFileParseResult.data.ref}
          {...(opts ?? {})}
        />
      );
    }
    case "number":
      return (
        <NumberDataDisplay
          value={value}
          {...(opts ?? {})}
        />
      );
    case "integer":
      return (
        <IntegerDataDisplay
          value={value}
          {...(opts ?? {})}
        />
      );
    case "geo_point":
      return (
        <GeocoordinateDataDisplay
          coordinates={value}
          {...(opts ?? {})}
        />
      );
    case "lookup": {
      return getLookupLinkForDisplay({
        lookupValue: value,
        emptyText: opts?.emptyText,
      });
    }
    case "multi-lookup": {
      if (!Array.isArray(value)) return opts?.emptyText ?? null;

      const multiLookupOpts = opts as
        | DataTypeToDataDisplayProps["multi-lookup"]
        | undefined;
      const { lookupEntityType, ...restMultiLookupOpts } =
        multiLookupOpts ?? {};

      // When values are raw UUID strings, fetch asset details to resolve names and links
      if (value.every((v) => typeof v === "string") && lookupEntityType) {
        return (
          <MultiLookupDataDisplay
            ids={value}
            entityType={lookupEntityType}
            emptyText={opts?.emptyText}
            {...restMultiLookupOpts}
          />
        );
      }

      const lookupLinks = value.map((lookupValue) => {
        return getLookupLinkForDisplay({
          lookupValue,
          emptyText: opts?.emptyText,
        });
      });

      return (
        <ArrayDataDisplay
          value={lookupLinks}
          {...(opts ?? {})}
        />
      );
    }
    case "date":
      return (
        <DateDataDisplay
          value={value}
          convertToUTC
          {...(opts ?? {})}
        />
      );
    case "date-time":
      return (
        <DateDataDisplay
          // assumption that value is in UTC time
          value={value}
          convertToUTC={false}
          {...(opts ?? {})}
        />
      );
    case "date-time-range": {
      const parsedValue = DateTimeValueSchemas.dateTimeRangeSimple
        .optional()
        .nullable()
        .safeParse(value);

      if (!parsedValue.success) return opts?.emptyText ?? null;

      return (
        <DateRangeDataDisplay
          value={
            value
              ? { from: parsedValue.data?.[0], to: parsedValue.data?.[1] }
              : value
          }
          convertToUTC
          {...(opts ?? {})}
        />
      );
    }
    case "number-array": {
      if (!Array.isArray(value)) return opts?.emptyText ?? null;

      return (
        <ArrayDataDisplay
          value={value}
          {...(opts ?? {})}
        />
      );
    }
    case "string":
    case "pick-list":
    default:
      return (
        <TextDataDisplay
          value={value}
          clamp={2}
          {...(opts ?? {})}
        />
      );
  }
};

/**
 * Display a value by attempting to parse the data type of the value. Note, this
 * may not be as reliable as explicitly specifying the data type and calling
 * {@link displayValueByDataType}
 * @param value the value to render
 * @param opts additional display options shared among all renderers, or that
 * are specific to one renderer
 * @returns renders the value using the appropriate renderer for the determined
 * data type
 */
export const displayValueWithUnknownDataType = (
  value: unknown,
  opts?: Omit<DataDisplayProps, "value"> & { strict?: boolean }
): React.ReactNode => {
  if (
    (!opts?.strict && z.boolean().safeParse(value).success) ||
    (opts?.strict && typeof value === "boolean")
  ) {
    return (
      <BooleanDataDisplay
        value={value as boolean}
        {...(opts ?? {})}
      />
    );
  }

  if (!opts?.strict && z.number().int().safeParse(Number(value)).success) {
    return (
      <IntegerDataDisplay
        alignment="right"
        value={value as number}
        {...(opts ?? {})}
      />
    );
  }

  if (
    (!opts?.strict && z.number().safeParse(Number(value)).success) ||
    (opts?.strict && typeof value === "number")
  ) {
    return (
      <NumberDataDisplay
        alignment="right"
        value={value as number}
        {...(opts ?? {})}
      />
    );
  }

  if (Array.isArray(value)) {
    if (GeocoordinateSimpleValueSchema.safeParse(value).success) {
      return (
        <GeocoordinateDataDisplay
          coordinates={value as [number | null, number | null]}
          {...(opts ?? {})}
        />
      );
    }

    if (DateTimeValueSchemas.dateTimeRangeSimple.safeParse(value).success) {
      return (
        <DateRangeDataDisplay
          value={{ from: value[0], to: value[1] }}
          {...(opts ?? {})}
        />
      );
    }

    if (z.array(FileUploadedSchema).safeParse(value).success) {
      return (
        <ArrayDataDisplay
          value={value.map((item) => (
            <FileDataDisplay
              key={item.ref}
              fileName={item.name}
              fileId={item.ref}
              prefetch={false}
              alignment="left"
              style={{ display: "inline-flex" }}
            />
          ))}
          {...(opts ?? {})}
        />
      );
    }

    return (
      <ArrayDataDisplay
        value={value}
        {...(opts ?? {})}
      />
    );
  }

  if (isPlainObject(value)) {
    if (FileUploadedSchema.safeParse(value).success) {
      return (
        <FileDataDisplay
          fileName={(value as FileUploadedType).name}
          fileId={(value as FileUploadedType).ref}
          {...(opts ?? {})}
        />
      );
    }
  }

  if (DateTimeValueSchemas.dateTime.safeParse(value).success) {
    return (
      <DateDataDisplay
        value={value as string}
        {...(opts ?? {})}
        convertToUTC
      />
    );
  }

  if (DateTimeValueSchemas.dateTimeStrict.safeParse(value).success) {
    return (
      <DateDataDisplay
        value={value as string}
        {...(opts ?? {})}
      />
    );
  }

  return value ?? opts?.emptyText ?? null;
};

export const getLookupLink = (
  id: string,
  name: string,
  resourceType: ResourceType
) => {
  switch (resourceType) {
    case Resources.FACILITY:
      return <RoutingLink to={linkToFacilities(id)}>{name}</RoutingLink>;
    case Resources.EQUIPMENT:
      return <RoutingLink to={linkToEquipmentDetail(id)}>{name}</RoutingLink>;
    case Resources.FLOW:
      return (
        <RoutingLink
          to={FlowDetailRoute.toLink({
            pathParams: { flowId: id },
          })}
        >
          {name}
        </RoutingLink>
      );
    case Resources.DEVICE:
      return <RoutingLink to={linkToDeviceDetail(id)}>{name}</RoutingLink>;
    case Resources.ASSET_GROUP:
    case Resources.NETWORK:
      return (
        <RoutingLink
          to={NetworkDetailRoute.toLink({
            pathParams: { networkId: id },
          })}
        >
          {name}
        </RoutingLink>
      );
    case Resources.USER_GROUP:
      return (
        <RoutingLink
          to={UserGroupDetailsRoutePath.toLink({
            pathParams: { groupId: id },
          })}
        >
          {name}
        </RoutingLink>
      );
    case Resources.USER_GROUP_CATEGORY:
      return (
        <RoutingLink
          to={UserGroupCategoryDetailsRoutePath.toLink({
            pathParams: { categoryId: id },
          })}
        >
          {name}
        </RoutingLink>
      );
    case Resources.ROLE:
      return <RoutingLink to={linkToRoleDetailPage(id)}>{name}</RoutingLink>;
    case Resources.USER:
      return <RoutingLink to={linkToUserDetailPage(id)}>{name}</RoutingLink>;
    case Resources.FORM_SUBMISSION:
      return (
        <RoutingLink to={linkToFormSubmissionDetail(id)}>{name}</RoutingLink>
      );
    case Resources.FORM_SCHEMA:
      return (
        <RoutingLink to={linkToFormTemplateDetail(id)}>{name}</RoutingLink>
      );
    case Resources.FORM_CATEGORY:
      return (
        <RoutingLink to={linkToFormCategoryDetails(id)}>{name}</RoutingLink>
      );
    case Resources.WORKFLOW:
      return (
        <RoutingLink
          to={WorkflowDetailsRoutePath.toLink({
            pathParams: { workflowId: id },
          })}
        >
          {name}
        </RoutingLink>
      );
    case Resources.WORKFLOW_TEMPLATE:
      return (
        <RoutingLink
          to={WorkflowTemplateDetailsRoutePath.toLink({
            pathParams: { workflowTemplateId: id },
          })}
        >
          {name}
        </RoutingLink>
      );
    case Resources.WORKFLOW_CATEGORY:
      return (
        <RoutingLink
          to={WorkflowCategoryDetailsRoutePath.toLink({
            pathParams: { workflowCategoryId: id },
          })}
        >
          {name}
        </RoutingLink>
      );
    case Resources.EVENT_CATEGORY:
      return (
        <RoutingLink to={linkToEventCategoryDetail(id)}>{name}</RoutingLink>
      );
    default:
      return name;
  }
};

/**
 * A function that returns the link to a lookup value.
 *
 * We have to make all these checks due to some historical inconsistency
 * with how data types are defined in the API.
 *
 * @param lookupValue the lookup value to get the link for.
 * @param emptyText the text to display if the lookup value is not valid.

 *
 * @example
 * 1. In form submission answers:
 * {
 *    "value": "c391ddc0-8c74-4905-8c45-68af941e62ca",
 *    "name": "AAAA Test",
 *    "entity_type": "facility"
 * }
 *
 * With question definition:
 * {
 *    "data_type": "lookup",
 *    "is_linked": false,
 *    "parent_facility_question_id": "...",
 *    "filter": {},
 *    "lookup_entity_type": "facility",
 *    "lookup_entity_attributes": ["name"]
 * }
 *
 * @example
 * 2. In custom attributes:
 * {
 *    "id": "c2583dc6-01af-4ca6-a456-50b82ba7ffde",
 *    "name": "A Big Compressor",
 *    "entity_type": "equipment"
 * }
 *
 * @example
 * 3. In reports:
 * {
 *    "id": "0eb100d1-7b55-4e54-a5ce-62e8c6fc8cba",
 *    "value": "Atlantic mackerel YHd0A46O6I"
 * }
 *
 * With input definition:
 * {
 *    "order": 2,
 *    "data_type": "lookup",
 *    "description": "The name of the facility",
 *    "is_required": false,
 *    "display_name": "Facility Name",
 *    "lookup_entity_type": "facility",
 *    "lookup_entity_attribute": "name"
 * }
 * @returns the link to the lookup value.
 */
const getLookupLinkForDisplay = ({
  lookupValue,
  emptyText,
}: {
  lookupValue:
    | LookupWithDetailsValueType
    | LookupWithSingleAttributeRefValueType
    | LookupAnswerValueType;
  emptyText?: string;
}) => {
  const lookupWithDetailsParseResult =
    LookupWithDetailsValueSchema.safeParse(lookupValue);
  const lookupWithSingleAttributeRefParseResult =
    LookupWithSingleAttributeRefValueSchema.safeParse(lookupValue);
  const lookupAnswerParseResult =
    LookupAnswerValueSchema.safeParse(lookupValue);

  if (lookupWithDetailsParseResult.success) {
    return getLookupLink(
      lookupWithDetailsParseResult.data.value,
      lookupWithDetailsParseResult.data.name,
      lookupWithDetailsParseResult.data.entity_type
    );
  } else if (lookupAnswerParseResult.success) {
    return getLookupLink(
      lookupAnswerParseResult.data.id,
      lookupAnswerParseResult.data.name,
      lookupAnswerParseResult.data.entity_type
    );
  } else if (lookupWithSingleAttributeRefParseResult.success) {
    return getLookupLink(
      lookupWithSingleAttributeRefParseResult.data.id,
      lookupWithSingleAttributeRefParseResult.data.value,
      lookupWithSingleAttributeRefParseResult.data.entity_type
    );
  } else {
    return emptyText ?? null;
  }
};

/**
 * A function that returns the localized string of an asset type.
 * @param assetType the asset type to localize.
 * @returns a localized string representing an asset type.
 */
export const displayLocalizedAssetType = (assetType?: AssetTypeType) => {
  const { localize } = useLocalization();
  switch (assetType) {
    case AssetType.FACILITY:
      return localize("facility_plural");
    case AssetType.EQUIPMENT:
      return localize("equipment_plural");
    case AssetType.DEVICE:
      return localize("device_plural");
    case AssetType.FLOW:
      return localize("flow_plural");
    case AssetType.ASSET_GROUP:
      return localize("asset_group_plural");
    default:
      return localize("asset_plural");
  }
};

export const displayPluralizedAssetType = (assetType?: AssetTypeType) => {
  switch (assetType) {
    case AssetType.FACILITY:
      return DefaultLocalizationMapping.facility_plural;
    case AssetType.EQUIPMENT:
      return DefaultLocalizationMapping.equipment_plural;
    case AssetType.DEVICE:
      return DefaultLocalizationMapping.device_plural;
    case AssetType.FLOW:
      return DefaultLocalizationMapping.flow_plural;
    case AssetType.ASSET_GROUP:
      return DefaultLocalizationMapping.asset_group_plural;
    default:
      return DefaultLocalizationMapping.asset_plural;
  }
};
